import type { EXPERIMENT_STATUSES } from 'definitions';

type ExperimentStatus = (typeof EXPERIMENT_STATUSES)[number];

/** The statuses a winner can be deployed from: the experiment has run (or is running). */
export const DEPLOYABLE_STATUSES = ['Running', 'Ended'] as const satisfies readonly ExperimentStatus[];

type DeployRefusalParams = {
  status: ExperimentStatus;
  startsAt: number | null;
  variants: ReadonlyArray<{ deployed: number | null }>;
};

/**
 * Why this experiment cannot be deployed, in words a person can act on; null when it can.
 * Shared by the results table (the Deploy button's disabled reason) and the server (the 409's
 * message), so the two cannot disagree.
 *
 * - A variant already deployed refuses whatever the status: ending a Deployed experiment makes
 *   it Ended but leaves its clone in place, so the status alone would allow a second clone.
 * - An experiment that never started has nothing to deploy from, even when it reads Ended.
 *
 * The server's guarantee is the same rule in SQL (deployableExperiment in lifecycle/deploy.ts);
 * this produces the message when that matches nothing.
 */
export function deployRefusal({ status, startsAt, variants }: DeployRefusalParams): string | null {
  if (variants.some((variant) => variant.deployed !== null)) return 'This experiment already has a deployed variant';
  if (startsAt === null) return 'This experiment never started, so it has no result to deploy';
  if (!(DEPLOYABLE_STATUSES as readonly ExperimentStatus[]).includes(status)) {
    return `Only a running or ended experiment can be deployed (this one is ${status})`;
  }
  return null;
}
